'use client';
import React, { useState } from 'react';
import AppNav from '@/components/AppNav';
import SafetyReminderBanner from '@/components/SafetyReminderBanner';
import SafetyAndFooter from '@/components/SafetyAndFooter';
import { Shield, Heart, AlertTriangle, Flag, Lock, Eye, Users, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import Link from 'next/link';

type SafetyTab = 'guidelines' | 'privacy' | 'terms' | 'tips';

const guidelines = [
  {
    title: 'Be Respectful and Honest',
    content: 'Treat every member with dignity and respect. Be honest about who you are, your intentions, and your background. Misrepresentation of any kind — including age, location, or relationship status — is not permitted.',
  },
  {
    title: 'No Harassment or Threatening Behavior',
    content: 'Harassment, threats, intimidation, or any form of abusive communication will result in immediate account suspension. This includes unwanted persistent contact after someone has asked you to stop.',
  },
  {
    title: 'Authentic Profiles Only',
    content: 'Use your real name (first name + last initial is acceptable), real photos, and accurate information. Fake profiles, catfishing, or impersonating others is strictly prohibited.',
  },
  {
    title: 'No Solicitation or Scams',
    content: 'JSN is a matchmaking and singles network, not a marketplace. Do not solicit money, gifts, or financial assistance from other members. Report any suspicious requests immediately.',
  },
  {
    title: 'Protect Your Personal Information',
    content: 'Do not share sensitive personal information (home address, financial details, workplace location) early in a connection. Take time to build trust before sharing private details.',
  },
  {
    title: 'Report Inappropriate Behavior',
    content: 'If you encounter a profile or message that violates these guidelines, use the Report button immediately. Our team reviews all reports and takes appropriate action.',
  },
  {
    title: 'Respect Cultural Boundaries',
    content: 'JSN serves a diverse Kenyan diaspora community with varied cultural, religious, and personal values. Respect differences in faith, background, and relationship expectations.',
  },
  {
    title: 'Age Requirement',
    content: 'All members must be 18 years of age or older. If you suspect a member is underage, report them immediately.',
  },
];

const safetyTips = [
  {
    icon: Eye,
    title: 'Take Your Time',
    tips: [
      'Get to know someone through messages before meeting in person.',
      'Do not rush into sharing personal contact information.',
      'Trust your instincts — if something feels off, it probably is.',
    ],
  },
  {
    icon: Shield,
    title: 'Meeting in Person',
    tips: [
      'Always meet in a public place for the first few meetings.',
      'Tell a trusted friend or family member where you are going.',
      'Arrange your own transportation to and from the meeting.',
      'Do not leave your drink unattended.',
    ],
  },
  {
    icon: AlertTriangle,
    title: 'Red Flags to Watch For',
    tips: [
      'Requests for money or financial assistance.',
      'Reluctance to video call or meet in person.',
      'Stories that seem inconsistent or change over time.',
      'Pressure to move off the platform quickly.',
      'Overly intense affection very early in the connection.',
    ],
  },
  {
    icon: Lock,
    title: 'Protect Your Privacy',
    tips: [
      'Do not share your home address, workplace, or daily routine early on.',
      'Use JSN messaging rather than personal phone numbers initially.',
      'Be cautious about sharing photos that reveal your location.',
    ],
  },
];

function AccordionItem({ title, content }: { title: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/20 transition-colors"
      >
        <span className="font-semibold text-foreground text-sm">{title}</span>
        {open ? <ChevronUp size={16} className="text-muted-foreground flex-shrink-0" /> : <ChevronDown size={16} className="text-muted-foreground flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-3">
          {content}
        </div>
      )}
    </div>
  );
}

export default function SafetyScreen() {
  const [activeTab, setActiveTab] = useState<SafetyTab>('guidelines');

  const tabs: { key: SafetyTab; label: string; icon: React.ElementType }[] = [
    { key: 'guidelines', label: 'Community Guidelines', icon: Users },
    { key: 'tips', label: 'Safety Tips', icon: Shield },
    { key: 'privacy', label: 'Privacy Policy', icon: Lock },
    { key: 'terms', label: 'Terms of Service', icon: Flag },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppNav activePage="safety" />

      <div className="pt-14">
        {/* Hero */}
        <div className="hero-gradient py-12 text-center px-4">
          <div className="max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-sm font-medium text-primary">
              <Shield size={14} />
              Safety & Trust
            </div>
            <h1 className="text-3xl font-black text-foreground">Your Safety is Our Priority</h1>
            <p className="text-muted-foreground text-base">
              JSN is committed to providing a safe, respectful, and authentic space for Kenyan diaspora singles.
            </p>
          </div>
        </div>

        <div className="max-w-screen-lg mx-auto px-4 lg:px-8 py-8">
          {/* Member Safety Reminder card — placed below hero, inside page content */}
          <div className="mb-8">
            <SafetyReminderBanner />
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {tabs.map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.key
                      ? 'bg-primary text-white' :'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                  }`}
                >
                  <TabIcon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Community Guidelines */}
          {activeTab === 'guidelines' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-bold text-foreground text-lg">Community Guidelines</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      These guidelines exist to protect every member of the JSN community. Violations may result in account suspension or permanent removal.
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {guidelines.map(g => (
                    <AccordionItem key={g.title} title={g.title} content={g.content} />
                  ))}
                </div>
              </div>

              {/* Report section */}
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <Flag size={20} className="text-primary" />
                  <h3 className="font-bold text-foreground">Report a Violation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  If you encounter a profile or behavior that violates our community guidelines, please report it immediately. You can report any profile directly from their profile page using the Report button.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link href="/discover-page" className="btn-primary px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
                    <Flag size={14} />
                    Report a Profile
                  </Link>
                  <a href="mailto:safety@jsnapp.co" className="btn-outline-red px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
                    <ExternalLink size={14} />
                    Email Safety Team
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Safety Tips */}
          {activeTab === 'tips' && (
            <div className="space-y-6">
              <div className="bg-accent/5 border border-accent/20 rounded-2xl p-5 flex items-start gap-3">
                <Heart size={20} className="text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Connecting with someone new should be exciting, not stressful.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    These tips are here to help you navigate online introductions safely and confidently as a Kenyan diaspora single.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {safetyTips.map(section => {
                  const SectionIcon = section.icon;
                  return (
                    <div key={section.title} className="bg-card border border-border rounded-2xl p-6 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <SectionIcon size={20} className="text-primary" />
                        </div>
                        <h3 className="font-bold text-foreground">{section.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {section.tips.map(tip => (
                          <li key={tip.slice(0, 30)} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
                <h3 className="font-bold text-foreground">Emergency Resources</h3>
                <p className="text-sm text-muted-foreground">
                  If you are in immediate danger, call 911. For non-emergency safety concerns related to JSN, contact our safety team.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href="mailto:safety@jsnapp.co" className="btn-outline-red px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2">
                    <Shield size={14} />
                    Contact Safety Team
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Privacy Policy */}
          {activeTab === 'privacy' && (
            <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <Lock size={20} className="text-primary" />
                <h2 className="font-bold text-foreground text-lg">Privacy Policy</h2>
              </div>
              <p className="text-xs text-muted-foreground">Last updated: May 2026</p>

              <div className="bg-muted/30 border border-border rounded-xl px-4 py-3">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Jamuhuri Singles Network is a service operated by Relyon LLC.
                </p>
              </div>

              {[
                {
                  title: '1. Information We Collect',
                  content: 'We collect information you provide when creating your profile, including your display name, email address, date of birth, gender, US state, Kenyan county background, relationship intention, faith, bio, and profile photo. We also collect usage data such as profiles viewed, likes given, and messages sent.',
                },
                {
                  title: '2. How We Use Your Information',
                  content: 'Your information is used to create and display your profile to other members, match you with compatible profiles, send you notifications about matches and messages, improve our platform, and ensure community safety. We do not sell your personal information to third parties.',
                },
                {
                  title: '3. Profile Visibility',
                  content: 'Your profile is visible to other registered JSN members. You control what information appears on your profile. Your email address is never displayed publicly. You can update or delete your profile information at any time.',
                },
                {
                  title: '4. Data Security',
                  content: 'We implement industry-standard security measures to protect your personal information. However, no online platform can guarantee 100% security. We encourage you to use a strong, unique password and to report any suspicious activity.',
                },
                {
                  title: '5. Cookies and Tracking',
                  content: 'We use cookies to maintain your session, remember your preferences, and analyze platform usage. You can control cookie settings through your browser. Some features may not function correctly if cookies are disabled.',
                },
                {
                  title: '6. Data Retention',
                  content: 'We retain your account data for as long as your account is active. If you delete your account, your profile data will be removed within 30 days, though some information may be retained for legal compliance purposes.',
                },
                {
                  title: '7. Contact Us',
                  content: 'For privacy-related questions or requests, contact us at privacy@jsnapp.co. We will respond within 30 days.',
                },
              ].map(section => (
                <div key={section.title} className="space-y-2">
                  <h3 className="font-semibold text-foreground text-sm">{section.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Terms of Service */}
          {activeTab === 'terms' && (
            <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <Flag size={20} className="text-primary" />
                <h2 className="font-bold text-foreground text-lg">Terms of Service</h2>
              </div>
              <p className="text-xs text-muted-foreground">Last updated: May 2026</p>

              <div className="bg-muted/30 border border-border rounded-xl px-4 py-3">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Jamuhuri Singles Network is a service operated by Relyon LLC.
                </p>
              </div>

              {[
                {
                  title: '1. Acceptance of Terms',
                  content: 'By creating an account on JSN (Jamuhuri Singles Network), you agree to these Terms of Service and our Community Guidelines. If you do not agree, you may not use the platform.',
                },
                {
                  title: '2. Eligibility',
                  content: 'You must be at least 18 years of age to use JSN. By registering, you confirm that you are 18 or older and that all information you provide is accurate and truthful.',
                },
                {
                  title: '3. Account Responsibility',
                  content: 'You are responsible for maintaining the security of your account credentials. You must not share your account with others or allow others to access your account. You are responsible for all activity that occurs under your account.',
                },
                {
                  title: '4. Prohibited Conduct',
                  content: 'You may not use JSN to harass, threaten, or harm other members; create fake profiles or impersonate others; solicit money or financial assistance; post illegal, offensive, or inappropriate content; use the platform for commercial solicitation; or attempt to circumvent any platform security measures.',
                },
                {
                  title: '5. Premium Membership',
                  content: 'Premium memberships are billed in advance for the selected plan duration. Plans renew automatically unless cancelled before the renewal date. Refunds are not provided for partial billing periods. You can cancel your subscription at any time through your account settings.',
                },
                {
                  title: '6. Content Ownership',
                  content: 'You retain ownership of content you post on JSN. By posting content, you grant JSN a non-exclusive license to display that content on the platform. You may not post content that infringes on the intellectual property rights of others.',
                },
                {
                  title: '7. Termination',
                  content: 'JSN reserves the right to suspend or terminate any account that violates these Terms of Service or our Community Guidelines, with or without notice. You may delete your account at any time.',
                },
                {
                  title: '8. Disclaimer',
                  content: 'JSN is a values-based matchmaking and singles network and does not guarantee matches, relationships, or any specific outcomes. We are not responsible for the conduct of members on or off the platform. Use JSN at your own discretion.',
                },
              ].map(section => (
                <div key={section.title} className="space-y-2">
                  <h3 className="font-semibold text-foreground text-sm">{section.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{section.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <SafetyAndFooter />
    </div>
  );
}
